import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import ScheduleRow from './ScheduleRow';
import HoursRow from './HoursRow';
import EarnedRow from './EarnedRow';
import TotalColumn from './TotalColumn';
import BudgetAllocationHeader from './BudgetAllocationHeader';
import { PCT_SLOT_WIDTH } from './pctLayout';

/** Normalize API percent to 0–100 int for inputs and comparisons. */
function toPctInt(v) {
  if (v == null || v === '') return 0;
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}

function pickLatestMeta(pkg) {
  const fAt = pkg.fieldPctUpdatedAt
    ? new Date(pkg.fieldPctUpdatedAt).getTime()
    : 0;
  const sAt = pkg.shopPctUpdatedAt
    ? new Date(pkg.shopPctUpdatedAt).getTime()
    : 0;
  if (fAt === 0 && sAt === 0) return null;
  if (fAt >= sAt && pkg.fieldPctUpdatedAt) {
    return { at: pkg.fieldPctUpdatedAt, by: pkg.fieldPctUpdatedBy };
  }
  if (pkg.shopPctUpdatedAt) {
    return { at: pkg.shopPctUpdatedAt, by: pkg.shopPctUpdatedBy };
  }
  return null;
}

const sectionLabel = (text) => (
  <Typography
    sx={{
      fontSize: '10px',
      fontWeight: 600,
      color: 'text.secondary',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      mb: 0.75,
    }}
  >
    {text}
  </Typography>
);

/**
 * Redesigned Update Work Package Progress modal (shop | field | total when prefab).
 * Persists via the same analytics PATCH endpoints as the legacy dialog.
 */
const WorkPackageProgressModal = ({
  open,
  pkg,
  isField,
  saving,
  error,
  onClose,
  onSave,
  /** Called when prefab budget allocation is confirmed (✓); PATCH budget split immediately. */
  onBudgetAllocationConfirm,
}) => {
  const [fieldEarnedPct, setFieldEarnedPct] = useState(0);
  const [shopEarnedPct, setShopEarnedPct] = useState(0);
  const [shopAllocPct, setShopAllocPct] = useState(0);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open || !pkg) return;
    setFieldEarnedPct(toPctInt(pkg.fieldPercentComplete));
    setShopEarnedPct(toPctInt(pkg.shopPercentComplete));
    setNote('');
    const total = (pkg.shopBudgetHrs || 0) + (pkg.fieldBudgetHrs || 0);
    const initShopPct =
      total > 0 ? Math.round(((pkg.shopBudgetHrs || 0) / total) * 100) : 0;
    setShopAllocPct(initShopPct);
  }, [open, pkg?.id]);

  useEffect(() => {
    if (!open || !pkg) return;
    const total = (pkg.shopBudgetHrs || 0) + (pkg.fieldBudgetHrs || 0);
    const initShopPct =
      total > 0 ? Math.round(((pkg.shopBudgetHrs || 0) / total) * 100) : 0;
    setShopAllocPct(initShopPct);
  }, [open, pkg?.id, pkg?.shopBudgetHrs, pkg?.fieldBudgetHrs]);

  const requiresShop = Boolean(pkg?.requiresShop);
  const canEditBudget = !isField || requiresShop;
  const totalBudgetHrs =
    (pkg?.shopBudgetHrs || 0) + (pkg?.fieldBudgetHrs || 0) || 0;

  const shopBudgetHrsAlloc = useMemo(() => {
    if (!pkg) return 0;
    if (!requiresShop) return pkg.shopBudgetHrs || 0;
    return Math.round(totalBudgetHrs * (shopAllocPct / 100));
  }, [pkg, requiresShop, totalBudgetHrs, shopAllocPct]);

  const fieldBudgetHrsAlloc = useMemo(() => {
    if (!pkg) return 0;
    if (!requiresShop) return pkg.fieldBudgetHrs || 0;
    return Math.max(0, totalBudgetHrs - shopBudgetHrsAlloc);
  }, [pkg, requiresShop, totalBudgetHrs, shopBudgetHrsAlloc]);

  const persistBudgetAlloc = useCallback(
    async (nextShopPct) => {
      if (!onBudgetAllocationConfirm || !pkg || totalBudgetHrs <= 0) return;
      const newShop = Math.round(totalBudgetHrs * (nextShopPct / 100));
      const newField = Math.max(0, totalBudgetHrs - newShop);
      await onBudgetAllocationConfirm(pkg.id, {
        newShopBudgetHrs: newShop,
        newFieldBudgetHrs: newField,
        notes: note.trim() || undefined,
      });
    },
    [onBudgetAllocationConfirm, pkg, totalBudgetHrs, note]
  );

  if (!pkg) return null;

  const fieldLocked = Boolean(pkg.fieldActualFinishDate);
  const shopLocked = Boolean(pkg.shopActualFinishDate);

  const initialFieldPct = toPctInt(pkg.fieldPercentComplete);
  const initialShopPct = toPctInt(pkg.shopPercentComplete);

  const fieldDirty = !fieldLocked && fieldEarnedPct !== initialFieldPct;
  const shopDirty =
    requiresShop && !shopLocked && shopEarnedPct !== initialShopPct;
  const hasNote = note.trim().length > 0;

  const noteOnly = hasNote && !fieldDirty && !shopDirty;

  const saveFieldPercent = fieldDirty || noteOnly;
  const saveShopPercent = shopDirty;

  const hasChanges = fieldDirty || shopDirty || hasNote;

  const handleSave = () => {
    onSave(pkg.id, {
      // A side that isn't being saved sends its stored baseline, never live
      // state, so a stale input value can't reach the update handler.
      fieldPercentComplete: saveFieldPercent ? fieldEarnedPct : initialFieldPct,
      shopPercentComplete:
        requiresShop && saveShopPercent ? shopEarnedPct : initialShopPct,
      saveFieldPercent,
      saveShopPercent: requiresShop && saveShopPercent,
      notes: note.trim() || undefined,
    });
  };

  const totalEarnedHrs = requiresShop
    ? shopBudgetHrsAlloc * (shopEarnedPct / 100) +
      fieldBudgetHrsAlloc * (fieldEarnedPct / 100)
    : fieldBudgetHrsAlloc * (fieldEarnedPct / 100);

  const totalActualHrs = (pkg.shopActualHrs || 0) + (pkg.fieldActualHrs || 0);

  const latest = pickLatestMeta(pkg);

  const fieldActualStart = Boolean(pkg.fieldActualStartDate);
  const fieldActualFinish = Boolean(pkg.fieldActualFinishDate);

  const cardShellSx = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'background.paper',
    overflow: 'hidden',
  };

  const cardTitleSx = {
    fontSize: '13px',
    fontWeight: 800,
    letterSpacing: '0.14em',
    lineHeight: 1.2,
  };

  const columnBody = (which) => {
    const start = which === 'shop' ? pkg.shopSchedStart : pkg.schedStart;
    const end = which === 'shop' ? pkg.shopSchedEnd : pkg.schedEnd;
    const budget = which === 'shop' ? shopBudgetHrsAlloc : fieldBudgetHrsAlloc;
    const actual =
      which === 'shop' ? pkg.shopActualHrs || 0 : pkg.fieldActualHrs || 0;
    const earnedPct = which === 'shop' ? shopEarnedPct : fieldEarnedPct;
    const setEarned = which === 'shop' ? setShopEarnedPct : setFieldEarnedPct;
    const locked = which === 'shop' ? shopLocked : fieldLocked;

    return (
      <Box
        sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        {sectionLabel('Schedule')}
        <ScheduleRow
          startDate={start}
          endDate={end}
          pctSlotWidth={PCT_SLOT_WIDTH}
          shopComplete={which === 'shop' && shopLocked}
          plannedStartDate={start}
          plannedEndDate={end}
          actualStartDate={
            which === 'shop'
              ? pkg.shopActualStartDate
              : pkg.fieldActualStartDate
          }
          actualFinishDate={
            which === 'shop'
              ? pkg.shopActualFinishDate
              : pkg.fieldActualFinishDate
          }
          showPlannedActualLayout
          reservePillSpace={requiresShop}
        />
        <HoursRow budgetHrs={budget} actualHrs={actual} />
        {sectionLabel('Earned')}
        <EarnedRow
          earnedPct={earnedPct}
          budgetHrs={budget}
          actualHrs={actual}
          onPctChange={setEarned}
          readOnly={locked}
          disabled={saving}
        />
      </Box>
    );
  };

  const MODAL_MAX_WIDTH_PX = 1380;
  const MODAL_MAX_WIDTH_FIELD_ONLY_PX = 740;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: requiresShop
            ? MODAL_MAX_WIDTH_PX
            : MODAL_MAX_WIDTH_FIELD_ONLY_PX,
          width: '100%',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 auto', pr: 1 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
              Update Work Package Progress
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: 'text.secondary',
                mt: 0.75,
                fontWeight: 500,
              }}
            >
              {pkg.autodeskName
                ? `${pkg.autodeskName} — ${pkg.name}`
                : pkg.name}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 1,
              flexShrink: 0,
              minWidth: 0,
              ...(!requiresShop ? { maxWidth: '100%' } : {}),
            }}
          >
            {latest ? (
              <Typography
                sx={{
                  fontSize: '11px',
                  color: 'text.secondary',
                  textAlign: 'right',
                  maxWidth: 220,
                }}
              >
                Updated{' '}
                {new Date(latest.at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
                {latest.by ? ` by ${latest.by}` : ''}
              </Typography>
            ) : null}
            <Chip
              size="small"
              label={requiresShop ? 'Prefab required' : 'No prefab'}
              color={requiresShop ? 'primary' : 'default'}
              variant={requiresShop ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, pointerEvents: 'none' }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {requiresShop && (
            <BudgetAllocationHeader
              open={open}
              shopAllocPct={shopAllocPct}
              onShopAllocPctChange={setShopAllocPct}
              onPersistShopAllocPct={
                canEditBudget && onBudgetAllocationConfirm
                  ? persistBudgetAlloc
                  : undefined
              }
              canEditBudget={canEditBudget}
              disabled={saving || totalBudgetHrs <= 0}
            />
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: requiresShop
                ? 'minmax(0, 1fr) minmax(0, 1fr) minmax(200px, 240px)'
                : 'minmax(0, 1fr)',
              gap: 2,
              alignItems: 'stretch',
              width: '100%',
              minWidth: 0,
            }}
          >
            {requiresShop && (
              <Card elevation={2} sx={cardShellSx}>
                <Box
                  sx={{
                    px: 2,
                    pt: 2,
                    pb: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{ ...cardTitleSx, textAlign: 'left', flexShrink: 0 }}
                  >
                    SHOP
                  </Typography>
                  {shopLocked ? (
                    <Chip
                      size="small"
                      label="✓ Complete"
                      sx={{
                        bgcolor: '#1D9E75',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '11px',
                        pointerEvents: 'none',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Chip
                      size="small"
                      label="● In Progress"
                      sx={{
                        bgcolor: '#0E7490',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '11px',
                        pointerEvents: 'none',
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
                <CardContent
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    pt: 1.5,
                    px: 2,
                    pb: 2,
                    '&:last-child': { pb: 2 },
                  }}
                >
                  {columnBody('shop')}
                </CardContent>
              </Card>
            )}

            <Card
              elevation={2}
              sx={{
                ...cardShellSx,
                ...(!requiresShop
                  ? {
                      width: '88%',
                      minWidth: 0,
                      maxWidth: '100%',
                      justifySelf: 'center',
                      boxSizing: 'border-box',
                    }
                  : {}),
              }}
            >
              <Box
                sx={{
                  px: 2,
                  pt: 2,
                  pb: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  component="h3"
                  sx={{ ...cardTitleSx, textAlign: 'left', flexShrink: 0 }}
                >
                  FIELD
                </Typography>
                {fieldActualStart && !fieldActualFinish ? (
                  <Chip
                    size="small"
                    label="● In Progress"
                    sx={{
                      bgcolor: '#0E7490',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '11px',
                      pointerEvents: 'none',
                      flexShrink: 0,
                    }}
                  />
                ) : !fieldActualStart ? (
                  <Chip
                    size="small"
                    label="○ Not Started"
                    variant="outlined"
                    sx={{
                      fontWeight: 600,
                      fontSize: '11px',
                      color: 'text.secondary',
                      borderColor: 'action.disabledBackground',
                      bgcolor: 'transparent',
                      pointerEvents: 'none',
                      flexShrink: 0,
                    }}
                  />
                ) : null}
              </Box>
              <CardContent
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  pt: 1.5,
                  px: 2,
                  pb: 2,
                  '&:last-child': { pb: 2 },
                }}
              >
                {columnBody('field')}
              </CardContent>
            </Card>

            {requiresShop && (
              <Card elevation={2} sx={cardShellSx}>
                <Box sx={{ px: 2, pt: 2, pb: 0 }}>
                  <Typography component="h3" sx={cardTitleSx}>
                    TOTAL
                  </Typography>
                </Box>
                <CardContent
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    pt: 1.5,
                    px: 2,
                    pb: 2,
                    '&:last-child': { pb: 2 },
                  }}
                >
                  <TotalColumn
                    budgetHrs={shopBudgetHrsAlloc + fieldBudgetHrsAlloc}
                    actualHrs={totalActualHrs}
                    earnedHrs={totalEarnedHrs}
                  />
                </CardContent>
              </Card>
            )}
          </Box>

          <Box sx={{ mt: 0 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 1 }}>
              Note
            </Typography>
            <TextField
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional)"
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              disabled={saving}
              sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
            />
          </Box>

          {error && (
            <Typography color="error" sx={{ fontSize: 12 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          Update Progress
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkPackageProgressModal;
